import ClubScoutingPanel from "@/components/ClubScoutingPanel";
import SeasonTimeline from "@/components/SeasonTimeline";
import {MatchAPI} from "@/api/match";
import OwnClubLink from "@/components/OwnClubLink";
import LinkIcon from "@mui/icons-material/Link";
import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import {
	Alert,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	Tooltip,
} from "@mui/material";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import {useCategories} from "@/utils/hooks";
import {PlayerAPI} from "@/api/player";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import VenueManager from "@/components/VenueManager";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";

import {ClubAPI} from "@/api/club";
import {RosterAPI} from "@/api/roster";
import {getPlayerDetailPath, visibleRosters} from "@/utils/functions";

import ArrowBack from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import {
	AREA_OPTIONS,
	ERROR_OPERATION,
	getInternalClubId,
} from "@/utils/constants";

import StatusBadge from "@/components/StatusBadge";

import "./index.scss";

// ── Role grouping ─────────────────────────────────────────────────────────────

const ROLE_GROUPS = [
	{
		key: "P",
		label: "Portieri",
		abbr: "P",
		match: (p) => p.goalkeeper === true || p.primaryRole === "Portiere",
	},
	{
		key: "D",
		label: "Difensori",
		abbr: "D",
		match: (p) => !p.goalkeeper && p.primaryRole === "Difensore",
	},
	{
		key: "C",
		label: "Centrocampisti",
		abbr: "C",
		match: (p) =>
			!p.goalkeeper &&
			["Mediano", "Centrocampista", "Trequartista"].includes(p.primaryRole),
	},
	{
		key: "A",
		label: "Attaccanti",
		abbr: "A",
		match: (p) => !p.goalkeeper && p.primaryRole === "Attaccante",
	},
];

function groupPlayersByRole(rosterPlayers) {
	const players = (rosterPlayers ?? []).map((rp) => ({
		...(rp.player ?? rp),
		jerseyNumber: rp.jerseyNumber ?? null,
	}));
	const result = {};
	for (const group of ROLE_GROUPS) {
		const matched = players.filter(group.match);
		if (matched.length) result[group.key] = matched;
	}
	// Ungrouped fallback (unknown role)
	const grouped = Object.values(result).flat();
	const ungrouped = players.filter((p) => !grouped.some((g) => g.id === p.id));
	if (ungrouped.length) result["?"] = ungrouped;
	return result;
}

// ── Stato del giocatore nella rosa ─────────────────────────────────────────────

const PLAYER_STATUS = {
	Interno: {tone: "good", label: "Interno"},
	Prestito_in: {tone: "neutral", label: "Prestito in entrata"},
	Prestito_out: {tone: "warning", label: "Prestito in uscita"},
	Ceduto: {tone: "critical", label: "Ceduto"},
};

function PlayerStatus({status}) {
	const cfg = PLAYER_STATUS[status];
	return cfg ? (
		<StatusBadge tone={cfg.tone} label={cfg.label} />
	) : (
		<StatusBadge label={status?.replace("_", " ") ?? "—"} />
	);
}

// ── Single roster card ────────────────────────────────────────────────────────

function RosterCard({
	roster,
	matches,
	matchesLoading,
	matchesError,
	navigate,
	onPlayerClick,
	onEdit,
	onManage,
}) {
	const competitions = (roster.competitionSeasons ?? [])
		.map((cs) => cs.competition?.name)
		.filter(Boolean);

	const grouped = useMemo(
		() => groupPlayersByRole(roster.players),
		[roster.players],
	);

	const totalPlayers = (roster.players ?? []).length;

	return (
		<div className="club-roster-card">
			<div className="club-roster-card__header">
				<div className="club-roster-card__header-left">
					<span className="club-roster-card__category">
						{roster.name || roster.playerCategory?.name || "Rosa"}
					</span>
					{competitions.length > 0 && (
						<span className="club-roster-card__competitions">
							<EmojiEventsIcon sx={{fontSize: 13}} />
							{competitions.join(" · ")}
						</span>
					)}
				</div>
				<span className="club-roster-card__count">
					<SportsSoccerIcon sx={{fontSize: 13}} />
					{totalPlayers} {totalPlayers === 1 ? "giocatore" : "giocatori"}
				</span>
				{onEdit && (
					<div className="club-roster-actions">
						<Tooltip title="Modifica rosa">
							<IconButton
								size="small"
								color="inherit"
								aria-label={"Modifica rosa " + roster.name}
								onClick={onEdit}
							>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Gestisci giocatori">
							<IconButton
								size="small"
								color="inherit"
								aria-label={"Gestisci giocatori " + roster.name}
								onClick={onManage}
							>
								<GroupIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</div>
				)}
			</div>

			{(matchesLoading ||
				matchesError ||
				matches.length > 0 ||
				competitions.length > 0) && (
				<section
					className="club-roster-card__timeline"
					aria-label={"Stagione gara per gara " + roster.name}
				>
					<h3>La stagione gara per gara</h3>
					{matchesLoading ? (
						<p>Caricamento partite…</p>
					) : matchesError ? (
						<Alert severity="error">Impossibile caricare le partite.</Alert>
					) : (
						<SeasonTimeline
							matches={matches}
							rosterId={roster.id}
							navigate={navigate}
						/>
					)}
				</section>
			)}

			{totalPlayers === 0 ? (
				<div className="club-roster-card__empty">Nessun giocatore</div>
			) : (
				<div className="club-roster-card__body">
					{ROLE_GROUPS.map((group) => {
						const players = grouped[group.key];
						if (!players?.length) return null;
						return (
							<div key={group.key} className="club-role-group">
								<div className="club-role-group__header">
									<span className="club-role-group__abbr">{group.abbr}</span>
									<span className="club-role-group__label">{group.label}</span>
									<span className="club-role-group__count">
										{players.length}
									</span>
								</div>
								<div className="club-role-group__players">
									{players
										.slice()
										.sort((a, b) =>
											(a.lastName ?? "").localeCompare(b.lastName ?? ""),
										)
										.map((p) => (
											<div
												key={p.id}
												className="club-player-row"
												onClick={() => onPlayerClick(p)}
											>
												{p.jerseyNumber && (
													<span className="club-player-row__jersey">
														#{p.jerseyNumber}
													</span>
												)}
												<span className="club-player-row__name">
													{p.lastName} {p.firstName}
												</span>
												{p.secondaryRole && (
													<span className="club-player-row__secondary">
														{p.secondaryRole}
													</span>
												)}
												{p.dateOfBirth && (
													<span className="club-player-row__year">
														{moment(p.dateOfBirth).format("YYYY")}
													</span>
												)}
												<PlayerStatus status={p.status} />
											</div>
										))}
								</div>
							</div>
						);
					})}

					{/* Ungrouped fallback */}
					{grouped["?"]?.length > 0 && (
						<div className="club-role-group">
							<div className="club-role-group__header">
								<span className="club-role-group__abbr">?</span>
								<span className="club-role-group__label">Altro</span>
								<span className="club-role-group__count">
									{grouped["?"].length}
								</span>
							</div>
							<div className="club-role-group__players">
								{grouped["?"].map((p) => (
									<div
										key={p.id}
										className="club-player-row"
										onClick={() => onPlayerClick(p)}
									>
										{p.jerseyNumber && (
											<span className="club-player-row__jersey">
												#{p.jerseyNumber}
											</span>
										)}
										<span className="club-player-row__name">
											{p.lastName} {p.firstName}
										</span>
										{p.secondaryRole && (
											<span className="club-player-row__secondary">
												{p.secondaryRole}
											</span>
										)}
										{p.dateOfBirth && (
											<span className="club-player-row__year">
												{moment(p.dateOfBirth).format("YYYY")}
											</span>
										)}
										<PlayerStatus status={p.status} />
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

function ClubPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {id} = useParams();

	const allSeasons = useSelector((state) => state?.seasons ?? []);
	const currentSeasonId = useSelector((state) => state?.season?.id);

	const seasonOptions = useMemo(
		() =>
			[...allSeasons]
				.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
				.map((s) => ({value: s.id, label: s.name})),
		[allSeasons],
	);

	const [selectedSeason, setSelectedSeason] = useState(null);
	const [loading, setLoading] = useState(false);
	const [linkOwnClub, setLinkOwnClub] = useState(false);
	const userRole = useSelector((s) => s?.user?.roleName?.toLowerCase());
	const [club, setClub] = useState(null);
	// roster objects enriched with full player data
	const [rosters, setRosters] = useState([]);
	const [clubMatches, setClubMatches] = useState([]);
	const [matchesLoading, setMatchesLoading] = useState(true);
	const [matchesError, setMatchesError] = useState(false);
	useEffect(() => {
		let active = true;
		setClubMatches([]);
		setMatchesError(false);
		setMatchesLoading(true);
		const fetchMatches = async () => {
			try {
				const response = await MatchAPI.list({
					clubId: id,
					seasonId: selectedSeason.value,
					includeDetails: false,
				});
				if (active)
					setClubMatches(
						(response || [])
							.filter((m) => m.startDate)
							.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
					);
			} catch {
				if (active) setMatchesError(true);
			} finally {
				if (active) setMatchesLoading(false);
			}
		};
		if (selectedSeason?.value) fetchMatches();
		else setMatchesLoading(false);
		return () => {
			active = false;
		};
	}, [id, selectedSeason]);

	const {categories} = useCategories({formatted: true});
	const [editingRoster, setEditingRoster] = useState(null);
	const [managingRoster, setManagingRoster] = useState(null);
	const [catalog, setCatalog] = useState([]);
	const [catalogReady, setCatalogReady] = useState(false);
	const [selectedPlayer, setSelectedPlayer] = useState(null);
	const [playerForm, setPlayerForm] = useState(null);
	const [duplicates, setDuplicates] = useState([]);
	const [confirmNew, setConfirmNew] = useState(false);
	const [saving, setSaving] = useState(false);
	const reportError = (error) =>
		handleNotification(
			error?.response?.data?.error || error?.message || ERROR_OPERATION,
			"error",
		);
	const normalizeName = (name) =>
		(name || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("it");
	const saveRoster = async () => {
		const name = editingRoster.name.trim();
		if (!name) return;
		if (
			rosters.some(
				(r) =>
					r.id !== editingRoster.id &&
					normalizeName(r.name) === normalizeName(name),
			)
		) {
			handleNotification(
				"Esiste già una rosa con questo nome per il club e la stagione.",
				"warning",
			);
			return;
		}
		setSaving(true);
		try {
			const body = {
				name,
				clubId: id,
				seasonId: selectedSeason.value,
				playerCategoryId: editingRoster.category?.value || null,
			};
			if (editingRoster.id) await RosterAPI.update(editingRoster.id, body);
			else await RosterAPI.create({...body, prospect: false});
			setEditingRoster(null);
			await getRosters(selectedSeason.value);
		} catch (error) {
			reportError(error);
		} finally {
			setSaving(false);
		}
	};
	const openPlayers = async (roster) => {
		setManagingRoster(roster);
		setSelectedPlayer(null);
		setPlayerForm(null);
		setDuplicates([]);
		setConfirmNew(false);
		setCatalogReady(false);
		setSaving(true);
		try {
			const [fresh, players] = await Promise.all([
				RosterAPI.getById(roster.id),
				PlayerAPI.list({seasonId: selectedSeason.value}),
			]);
			setManagingRoster(fresh);
			setCatalog(players || []);
			setCatalogReady(true);
		} catch (error) {
			reportError(error);
		} finally {
			setSaving(false);
		}
	};
	const refreshPlayers = async () => {
		setManagingRoster(await RosterAPI.getById(managingRoster.id));
		await getRosters(selectedSeason.value);
	};
	const addExistingPlayer = async (playerId) => {
		setSaving(true);
		try {
			await RosterAPI.addPlayers([{rosterId: managingRoster.id, playerId}]);
			setSelectedPlayer(null);
			setPlayerForm(null);
			setDuplicates([]);
			setConfirmNew(false);
			await refreshPlayers();
		} catch (error) {
			reportError(error);
		} finally {
			setSaving(false);
		}
	};
	const createPlayer = async () => {
		setSaving(true);
		try {
			const players =
				(await PlayerAPI.list({seasonId: selectedSeason.value})) || [];
			setCatalog(players);
			const possible = players.filter(
				(p) =>
					normalizeName(p.firstName) === normalizeName(playerForm.firstName) &&
					normalizeName(p.lastName) === normalizeName(playerForm.lastName) &&
					(!playerForm.dateOfBirth ||
						!p.dateOfBirth ||
						moment(p.dateOfBirth).format("YYYY-MM-DD") ===
							playerForm.dateOfBirth),
			);
			if (possible.length && !confirmNew) {
				setDuplicates(possible);
				return;
			}
			await PlayerAPI.create({
				firstName: playerForm.firstName.trim(),
				lastName: playerForm.lastName.trim(),
				dateOfBirth: playerForm.dateOfBirth || null,
				goalkeeper: playerForm.primaryRole?.value === "Goalkeeper",
				primaryRole: playerForm.primaryRole?.label || null,
				playerCategoryId: null,
				rosterId: managingRoster.id,
			});
			setPlayerForm(null);
			setDuplicates([]);
			setConfirmNew(false);
			setCatalog(
				(await PlayerAPI.list({seasonId: selectedSeason.value})) || [],
			);
			await refreshPlayers();
		} catch (error) {
			reportError(error);
		} finally {
			setSaving(false);
		}
	};
	const removePlayer = async (playerId) => {
		setSaving(true);
		try {
			await RosterAPI.deletePlayer(managingRoster.id, playerId, false);
			await refreshPlayers();
		} catch (error) {
			reportError(error);
		} finally {
			setSaving(false);
		}
	};
	const changePlayer = (key, value) => {
		setPlayerForm((prev) => ({...prev, [key]: value}));
		setDuplicates([]);
		setConfirmNew(false);
	};

	// Init selected season
	useEffect(() => {
		if (seasonOptions.length && !selectedSeason) {
			const current =
				seasonOptions.find((s) => s.value === currentSeasonId) ??
				seasonOptions[0];
			setSelectedSeason(current);
		}
	}, [seasonOptions, currentSeasonId]);

	const getClub = async () => {
		try {
			const response = await ClubAPI.getById(id);
			setClub(response);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getRosters = async (seasonId) => {
		if (!seasonId) return;
		setLoading(true);
		try {
			// Fetch list for this club + season
			const list = await RosterAPI.list({seasonId, clubId: id});
			const filtered = (list ?? []).filter((r) => !r.clubId || r.clubId === id);

			// Enrich each roster with full player data via getById
			const enriched = await Promise.all(
				filtered.map(async (r) => {
					try {
						return await RosterAPI.getById(r.id);
					} catch {
						return r; // fallback to basic data
					}
				}),
			);

			setRosters(enriched);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getClub();
	}, [id]);

	useEffect(() => {
		if (selectedSeason) getRosters(selectedSeason.value);
	}, [id, selectedSeason]);

	const isInternal = id === getInternalClubId() || club?.internal;

	const mainActions = [
		{
			icon: <ArrowBack />,
			title: "Indietro",
			onClick: () => navigate("/settings/clubs"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate(`/settings/clubs/${id}/edit`),
		},
	];

	if (!isInternal && ["admin", "superuser"].includes(userRole))
		mainActions.push({
			icon: <LinkIcon />,
			title: "Collega al mio club",
			onClick: () => setLinkOwnClub(true),
		});
	const canManage = !isInternal;
	const sortedRosters = visibleRosters(rosters);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[{label: "Club", path: "/settings/clubs"}, {label: "/"}]}
				current={club?.name ?? ""}
				actions={<MainActions actions={mainActions} />}
			/>

			{/* Club header */}
			<div className="club-detail-header">
				<div className="club-detail-header__title">
					{club?.name ?? "—"}
					{isInternal && (
						<span className="club-detail-header__badge">Interno</span>
					)}
				</div>

				{/* Season selector */}
				<div className="club-detail-header__season">
					<VLFSelectNew
						options={seasonOptions}
						value={selectedSeason}
						onChange={(opt) => setSelectedSeason(opt)}
						isClearable={false}
						sorted={false}
						placeholder="Stagione…"
						controlStyles={{marginBottom: "0px"}}
					/>
				</div>
			</div>

			<ClubScoutingPanel clubId={id} clubName={club?.name} onSaved={getClub} />
			<VenueManager clubId={id} handleNotification={handleNotification} />
			{canManage && (
				<div className="d-flex justify-content-end mb-2">
					<Tooltip title="Aggiungi rosa">
						<IconButton
							aria-label="Aggiungi rosa"
							disabled={!selectedSeason || loading}
							onClick={() => setEditingRoster({name: "", category: null})}
						>
							<AddIcon />
						</IconButton>
					</Tooltip>
				</div>
			)}
			{/* Rosters */}
			{!loading && sortedRosters.length === 0 ? (
				<div className="club-empty">Nessuna rosa per questa stagione.</div>
			) : (
				<div
					className={
						"club-rosters-grid" +
						(sortedRosters.length === 1 ? " club-rosters-grid--single" : "")
					}
				>
					{sortedRosters.map((roster) => (
						<RosterCard
							key={roster.id}
							roster={roster}
							matches={clubMatches.filter(
								(m) =>
									m.homeRosterId === roster.id || m.awayRosterId === roster.id,
							)}
							matchesLoading={matchesLoading}
							matchesError={matchesError}
							navigate={navigate}
							onEdit={
								canManage
									? () =>
											setEditingRoster({
												...roster,
												category:
													categories.find(
														(c) => c.value === roster.playerCategoryId,
													) || null,
											})
									: undefined
							}
							onManage={canManage ? () => openPlayers(roster) : undefined}
							onPlayerClick={async (p) => {
								try {
									const player = await PlayerAPI.getById(p.id, {
										seasonId: selectedSeason.value,
									});
									navigate(getPlayerDetailPath(player));
								} catch (error) {
									reportError(error);
								}
							}}
						/>
					))}
				</div>
			)}
			<VLFModal
				open={!!editingRoster}
				title={editingRoster?.id ? "Modifica rosa" : "Nuova rosa"}
				close={() => !saving && setEditingRoster(null)}
				disabled={saving || !editingRoster?.name?.trim()}
				confirm={saveRoster}
				content={
					<div className="row g-2">
						<p>
							{club?.name} · {selectedSeason?.label}
						</p>
						<div className="col-12">
							<VLFTextField
								label="Nome rosa"
								placeholder="Nome rosa"
								value={editingRoster?.name || ""}
								required
								disabled={saving}
								onChange={(e) =>
									setEditingRoster({
										...editingRoster,
										name: e.target.value.slice(0, 128),
									})
								}
							/>
						</div>
						<div className="col-12">
							<VLFSelectNew
								label="Categoria (facoltativa)"
								options={categories}
								value={editingRoster?.category}
								isDisabled={saving}
								onChange={(category) =>
									setEditingRoster({...editingRoster, category})
								}
							/>
						</div>
					</div>
				}
			/>
			<VLFModal
				open={!!managingRoster}
				maxWidth="md"
				title={"Giocatori · " + (managingRoster?.name || "")}
				close={() => !saving && setManagingRoster(null)}
				cancelLabel="Chiudi"
				content={
					<div>
						<p>
							{club?.name} · {selectedSeason?.label}
						</p>
						{(managingRoster?.players || []).map((row) => (
							<div
								className="d-flex align-items-center justify-content-between gap-2"
								key={row.playerId || row.player?.id}
							>
								<span>
									{row.player?.firstName} {row.player?.lastName}
								</span>
								<Tooltip title="Rimuovi dalla rosa, conserva l’anagrafica">
									<IconButton
										aria-label={"Rimuovi dalla rosa " + row.player?.lastName}
										disabled={saving}
										onClick={() => removePlayer(row.playerId || row.player.id)}
									>
										<RemoveCircleOutlineIcon />
									</IconButton>
								</Tooltip>
							</div>
						))}
						<hr />
						<div className="d-flex align-items-center gap-2">
							<div className="flex-grow-1">
								<VLFSelectNew
									label="Calciatore già censito"
									options={catalog
										.filter(
											(p) =>
												!(managingRoster?.players || []).some(
													(r) => (r.playerId || r.player?.id) === p.id,
												),
										)
										.map((p) => ({
											value: p.id,
											label:
												(p.firstName || "") +
												" " +
												p.lastName +
												(p.dateOfBirth
													? " · " + moment(p.dateOfBirth).format("DD/MM/YYYY")
													: ""),
										}))}
									value={selectedPlayer}
									onChange={setSelectedPlayer}
									isDisabled={saving || !catalogReady}
								/>
							</div>
							<Tooltip title="Aggiungi alla rosa">
								<IconButton
									aria-label="Aggiungi calciatore selezionato"
									disabled={saving || !catalogReady || !selectedPlayer}
									onClick={() => addExistingPlayer(selectedPlayer.value)}
								>
									<AddIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Nuovo calciatore">
								<IconButton
									aria-label="Nuovo calciatore"
									disabled={saving || !catalogReady}
									onClick={() => {
										setPlayerForm({
											firstName: "",
											lastName: "",
											dateOfBirth: "",
											primaryRole: null,
										});
										setDuplicates([]);
										setConfirmNew(false);
									}}
								>
									<PersonAddIcon />
								</IconButton>
							</Tooltip>
						</div>
						{playerForm && (
							<div className="row g-2 mt-2">
								<div className="col-12">
									<p>Nuovo calciatore da associare a questa rosa esterna.</p>
								</div>
								<div className="col-6">
									<VLFTextField
										label="Nome"
										placeholder="Nome"
										required
										disabled={saving}
										value={playerForm.firstName}
										onChange={(e) => changePlayer("firstName", e.target.value)}
									/>
								</div>
								<div className="col-6">
									<VLFTextField
										label="Cognome"
										placeholder="Cognome"
										required
										disabled={saving}
										value={playerForm.lastName}
										onChange={(e) => changePlayer("lastName", e.target.value)}
									/>
								</div>
								<div className="col-6">
									<VLFTextField
										type="date"
										label="Data di nascita (facoltativa)"
										value={playerForm.dateOfBirth}
										disabled={saving}
										onChange={(e) =>
											changePlayer("dateOfBirth", e.target.value)
										}
									/>
								</div>
								<div className="col-6">
									<VLFSelectNew
										label="Ruolo (facoltativo)"
										options={AREA_OPTIONS}
										sorted={false}
										value={playerForm.primaryRole}
										isDisabled={saving}
										onChange={(role) => changePlayer("primaryRole", role)}
									/>
								</div>
								{duplicates.length > 0 && (
									<div className="col-12">
										<Alert severity="warning">
											Possibili anagrafiche già presenti. Puoi riutilizzarle
											oppure confermare che si tratta di un’altra persona.
										</Alert>
										{duplicates.map((p) => (
											<Button
												key={p.id}
												disabled={
													saving ||
													(managingRoster?.players || []).some(
														(r) => (r.playerId || r.player?.id) === p.id,
													)
												}
												onClick={() => addExistingPlayer(p.id)}
											>
												Usa {p.firstName} {p.lastName}
												{p.dateOfBirth
													? " · " + moment(p.dateOfBirth).format("DD/MM/YYYY")
													: ""}
											</Button>
										))}
										<FormControlLabel
											control={
												<Checkbox
													checked={confirmNew}
													disabled={saving}
													onChange={(e) => setConfirmNew(e.target.checked)}
												/>
											}
											label="È un’altra persona: crea una nuova anagrafica"
										/>
									</div>
								)}
								<div className="col-12 d-flex justify-content-end gap-2">
									<Button disabled={saving} onClick={() => setPlayerForm(null)}>
										Annulla
									</Button>
									<Button
										variant="contained"
										disabled={
											saving ||
											!playerForm.firstName.trim() ||
											!playerForm.lastName.trim() ||
											(duplicates.length > 0 && !confirmNew)
										}
										onClick={createPlayer}
									>
										Crea e aggiungi
									</Button>
								</div>
							</div>
						)}
					</div>
				}
			/>
			{linkOwnClub && (
				<OwnClubLink
					clubId={id}
					seasonId={selectedSeason?.value}
					close={() => setLinkOwnClub(false)}
					onLinked={() => {
						setLinkOwnClub(false);
						navigate("/settings/clubs/" + getInternalClubId());
					}}
				/>
			)}
		</Page>
	);
}

export default ClubPage;
